import { Check, ChevronDown, Search } from 'lucide-react'
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import './combobox.css'

export type ComboOption = {
  value: string
  /** Primary text, searched. */
  label: string
  /** Secondary text shown in the list and searched, but NOT on the trigger. */
  hint?: string
  /** Compact secondary for the closed trigger — a trigger should read as a
   *  value, not repeat every detail the list needs in order to be searchable. */
  short?: string
}

/**
 * A searchable single-select. Used where the list is long enough that scanning
 * it is work — 60 Shabbosos, where you usually know the parsha you want.
 *
 * Keyboard: type to filter, ↑/↓ to move, Enter to choose, Escape to close the
 * list and nothing else. The trigger is a button until it opens, so the closed
 * state reads as a value rather than an empty text field. `freeText` makes it
 * a field instead, whose list only suggests what to type.
 */
export function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Search…',
  onQueryChange,
  emptyHint,
  bilingual,
  id,
  freeText,
  inputType,
  'aria-invalid': invalid,
  'aria-describedby': describedBy,
}: {
  value: string | null
  onChange: (value: string) => void
  options: ComboOption[]
  placeholder?: string
  /**
   * What the person typed, for a list that lives on the SERVER.
   *
   * Given, the caller re-queries and hands back new `options` — the filtering
   * below then narrows what arrived, which changes nothing about what is shown
   * because the server already matched it. Omitted, the whole list was passed in
   * and filtering here is the whole story.
   *
   * This exists so a searchable picker over a roster is still THIS component. The
   * alternative — and what Members did — is a hand-rolled input with an
   * absolutely-positioned result list beside it, which is how an app ends up with
   * four pickers that dismiss differently.
   */
  onQueryChange?: (query: string) => void
  /** Shown in place of "No matches" before anything has been typed. */
  emptyHint?: string
  /**
   * The options pair the two scripts — a person beside their Hebrew name, a
   * parsha beside its English gloss. Plex Sans Hebrew carries both, so the pair
   * takes one family; left on the UI stack it splits across Inter and Plex,
   * which is two faces inside one name.
   *
   * The picker declares it rather than the option, because whether a list is
   * bilingual is a fact about the list: one option happening to hold no Hebrew
   * must not set it in a different face from the one above it.
   */
  bilingual?: boolean
  id?: string
  /**
   * The value is what is TYPED, and the list only offers ways to fill it in:
   * an address somebody may have shared with before, or one nobody has. The
   * field stays a field, Enter with nothing marked submits its form, and
   * picking an option writes that option's value into it.
   */
  freeText?: boolean
  /** The field's own type where the text is typed, such as `email`. */
  inputType?: 'text' | 'email'
  /** Set by `Field` on the control it labels, for a problem it states. */
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}) {
  const described = { 'aria-invalid': invalid, 'aria-describedby': describedBy }
  if (freeText)
    return (
      <TypedCombobox
        value={value ?? ''}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        bilingual={bilingual}
        id={id}
        inputType={inputType}
        described={described}
      />
    )
  return (
    <PickCombobox
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      onQueryChange={onQueryChange}
      emptyHint={emptyHint}
      bilingual={bilingual}
      id={id}
      described={described}
    />
  )
}

type Described = {
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

/** Keeps the marked option in view as the arrows walk past the list's edge. */
function useMarkedInView(listId: string, active: number, open: boolean) {
  useEffect(() => {
    if (!open || active < 0) return
    document
      .getElementById(`${listId}-${active}`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [listId, active, open])
}

// Fold away punctuation and case so "Reeh" finds "Re'eh" and "matot masei"
// finds "Matot-Masei". Hebrew is unaffected.
const fold = (t: string) => t.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, '')

function matching(options: ComboOption[], query: string) {
  const q = fold(query)
  if (!q) return options
  return options.filter((o) => fold(`${o.label} ${o.hint ?? ''}`).includes(q))
}

/** The option's label over its hint, as both variants draw it. */
function OptionText({ option }: { option: ComboOption }) {
  return (
    <span className="combo__option-main">
      <span className="combo__label combo__option-label">{option.label}</span>
      {option.hint ? (
        <span className="combo__hint combo__hint--block">{option.hint}</span>
      ) : null}
    </span>
  )
}

/**
 * The typed variant. Nothing is marked until an arrow key marks it, so Enter
 * on a typed address submits rather than swapping it for the first suggestion.
 * The marked option is announced through `aria-activedescendant`, focus never
 * leaving the field, and pressing an option keeps it there too.
 */
function TypedCombobox({
  value,
  onChange,
  options,
  placeholder,
  bilingual,
  id,
  inputType = 'text',
  described,
}: {
  value: string
  onChange: (value: string) => void
  options: ComboOption[]
  placeholder: string
  bilingual?: boolean
  id?: string
  inputType?: 'text' | 'email'
  described: Described
}) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  const listId = useId()
  const shown = useMemo(() => matching(options, value), [options, value])
  const listed = open && shown.length > 0
  /* A list that changes under a mark, as when somebody just shared is dropped
     from it, unmarks: the slot would otherwise hold a different person. */
  const shownKey = shown.map((o) => o.value).join('\n')
  useEffect(() => {
    if (shownKey !== undefined) setActive(-1)
  }, [shownKey])
  useMarkedInView(listId, active, listed)

  const choose = (v: string) => {
    onChange(v)
    setOpen(false)
    setActive(-1)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && listed) {
      e.preventDefault()
      setOpen(false)
      setActive(-1)
      return
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!listed) return setOpen(true)
      const delta = e.key === 'ArrowDown' ? 1 : -1
      setActive((i) => Math.min(shown.length - 1, Math.max(-1, i + delta)))
      return
    }
    if (e.key === 'Enter' && listed && active >= 0) {
      const hit = shown[active]
      if (hit) {
        e.preventDefault()
        choose(hit.value)
      }
    }
  }

  return (
    <div className="combo" data-bilingual={bilingual ? '' : undefined}>
      <input
        id={id}
        type={inputType}
        className="control combo__field"
        role="combobox"
        autoComplete="off"
        {...described}
        aria-expanded={listed}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={
          listed && active >= 0 ? `${listId}-${active}` : undefined
        }
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          onChange(e.target.value)
          setOpen(true)
          setActive(-1)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          setOpen(false)
          setActive(-1)
        }}
        onKeyDown={onKeyDown}
      />
      {listed && (
        <div
          className="combo__list"
          id={listId}
          role="listbox"
          /* Pressing anywhere in the list, its padding or its scrollbar, must
             not take focus from the field, or the blur closes the list. */
          onMouseDown={(e) => e.preventDefault()}
        >
          {shown.map((o, i) => (
            <div key={o.value}>
              <button
                type="button"
                role="option"
                id={`${listId}-${i}`}
                tabIndex={-1}
                aria-selected={i === active}
                className="combo__option"
                data-active={i === active ? '' : undefined}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(o.value)}
              >
                <OptionText option={o} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * The picking variant: the value is one of the options, and the closed field
 * is a button reading as that value.
 */
function PickCombobox({
  value,
  onChange,
  options,
  placeholder,
  onQueryChange,
  emptyHint,
  bilingual,
  id,
  described,
}: {
  value: string | null
  onChange: (value: string) => void
  options: ComboOption[]
  placeholder: string
  onQueryChange?: (query: string) => void
  emptyHint?: string
  bilingual?: boolean
  id?: string
  described: Described
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  const selected = options.find((o) => o.value === value) ?? null

  /* Held in a ref so the open-effect below does not depend on a callback the
     caller rewrites every render — the pattern `PageBack` documents. */
  const notify = useRef(onQueryChange)
  notify.current = onQueryChange
  const setSearch = (next: string) => {
    setQuery(next)
    notify.current?.(next)
  }

  const filtered = useMemo(() => matching(options, query), [options, query])
  useMarkedInView(listId, active, open)

  // Close on outside click or Escape, and reset the query each time it opens.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  /*
   * Opening clears the query, marks the current value and takes focus — and it
   * happens ON OPEN, nothing else.
   *
   * The options and the value are read through a ref rather than depended on, and
   * that is load-bearing rather than tidy: a caller that searches the server hands
   * back a NEW options array for every keystroke, so an effect depending on their
   * identity re-ran mid-typing and cleared the field it had just been typed into.
   * Nothing could be searched for at all.
   */
  const latest = useRef({ options, value })
  latest.current = { options, value }
  useEffect(() => {
    if (!open) return
    setQuery('')
    notify.current?.('')
    setActive(
      Math.max(
        0,
        latest.current.options.findIndex(
          (o) => o.value === latest.current.value,
        ),
      ),
    )
    inputRef.current?.focus()
  }, [open])

  const choose = (v: string) => {
    onChange(v)
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault()
      return setOpen(false)
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) return setOpen(true)
      const delta = e.key === 'ArrowDown' ? 1 : -1
      setActive((i) => Math.min(filtered.length - 1, Math.max(0, i + delta)))
    }
    if (e.key === 'Enter' && open) {
      e.preventDefault()
      const hit = filtered[active]
      if (hit) choose(hit.value)
    }
  }

  return (
    <div
      className="combo"
      data-bilingual={bilingual ? '' : undefined}
      ref={rootRef}
    >
      {open ? (
        <div className="combo__search">
          <Search className="combo__search-icon" aria-hidden />
          <input
            ref={inputRef}
            id={id}
            className="control combo__input"
            role="combobox"
            {...described}
            aria-expanded
            aria-controls={listId}
            aria-autocomplete="list"
            placeholder={placeholder}
            onKeyDown={onKeyDown}
            value={query}
            onChange={(e) => {
              setSearch(e.target.value)
              setActive(0)
            }}
          />
        </div>
      ) : (
        <button
          type="button"
          id={id}
          className="control combo__trigger"
          {...described}
          aria-haspopup="listbox"
          aria-expanded={false}
          onKeyDown={onKeyDown}
          onClick={() => setOpen(true)}
        >
          <span className="combo__value">
            {selected ? (
              <>
                <span className="combo__label">{selected.label}</span>
                {selected.short ? (
                  <span className="combo__hint">{selected.short}</span>
                ) : null}
              </>
            ) : (
              <span className="combo__hint">{placeholder}</span>
            )}
          </span>
          <ChevronDown className="combo__chevron" aria-hidden />
        </button>
      )}

      {open && (
        <div className="combo__list" id={listId} role="listbox">
          {filtered.length === 0 && (
            <p className="combo__empty">
              {query
                ? `No matches for “${query}”`
                : (emptyHint ?? 'No options')}
            </p>
          )}
          {filtered.map((o, i) => (
            <div key={o.value}>
              <button
                type="button"
                role="option"
                id={`${listId}-${i}`}
                aria-selected={o.value === value}
                className="combo__option"
                data-active={i === active ? '' : undefined}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(o.value)}
              >
                <OptionText option={o} />
                {o.value === value ? (
                  <Check className="combo__check" aria-hidden />
                ) : null}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
